import type { ChatMessage, ChatSource } from "../api/types";

/** Sentinel scope value meaning "All books" (category_id: null on the wire). */
export const ALL_SCOPE = "all";

/** Sentinel scope value meaning "the books I picked by hand". */
export const PICKED_SCOPE = "picked";

/** Client-side chat turn — adds UI-only `sources` on top of the wire ChatMessage shape. */
export interface ChatEntry extends ChatMessage {
  sources?: ChatSource[];
}

const SCOPE_KEY = "boort:chat:scope";
const PICKED_KEY = "boort:chat:picked";
const MAX_MESSAGES = 40;

export function getChatScope(): string {
  try {
    return localStorage.getItem(SCOPE_KEY) || ALL_SCOPE;
  } catch {
    return ALL_SCOPE;
  }
}

export function setChatScope(scope: string): void {
  try {
    localStorage.setItem(SCOPE_KEY, scope);
  } catch {
    // ignore — localStorage unavailable (private mode, etc.)
  }
}

export function getPickedBooks(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(PICKED_KEY) || "[]");
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

export function setPickedBooks(ids: string[]): void {
  try {
    localStorage.setItem(PICKED_KEY, JSON.stringify(ids));
  } catch {
    // ignore - localStorage unavailable (private mode, etc.)
  }
}

function messagesKey(scope: string): string {
  return `boort:chat:${scope}`;
}

export function getChatMessages(scope: string): ChatEntry[] {
  try {
    const raw = localStorage.getItem(messagesKey(scope));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as ChatEntry[];
  } catch {
    return [];
  }
}

/** Persists the conversation for a scope, capped at the most recent MAX_MESSAGES turns. */
export function setChatMessages(scope: string, messages: ChatEntry[]): void {
  try {
    const capped = messages.slice(-MAX_MESSAGES);
    localStorage.setItem(messagesKey(scope), JSON.stringify(capped));
  } catch {
    // ignore
  }
}

export function clearChatMessages(scope: string): void {
  try {
    localStorage.removeItem(messagesKey(scope));
  } catch {
    // ignore
  }
}
